function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useState, useLayoutEffect, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { get, isEmpty } from "lodash";
import { Input, Divider } from "semantic-ui-react";
import { useRect } from "@reach/rect";
import { ExpandMore, Search, Close } from "../../icons";
import MultiDownshift from "./MultiDownshift";
import CheckboxIcon from "../checkboxicon/CheckboxIcon";
import Tag from "./Tag";
import { Label } from "../label/Label";
import { onEnter, onSpace, useId } from "../../helpers";
export const MAX_OPTIONS_HEIGHT = 254;
const INPUT_HEIGHT = 38;
const BORDER_HEIGHT = 1;
export const MAX_RESULTS_BOX_HEIGHT = MAX_OPTIONS_HEIGHT + INPUT_HEIGHT + BORDER_HEIGHT * 2;
function useControlledState(propsValue, onChange) {
  const [value, setValue] = useState(propsValue);
  function onValueChange(value) {
    if (onChange) onChange(value);else setValue(value);
  }
  useEffect(() => {
    if (propsValue !== value) setValue(propsValue);
  }, [propsValue]);
  return [value, onValueChange];
}
const stringifyValue = value => typeof value === "string" ? value : value !== null && JSON.stringify(value) || "";
const RenderHiddenFields = _ref => {
  let {
    name,
    joinValues,
    valueKey,
    disabled,
    delimiter,
    selectedItems
  } = _ref;
  if (!name) return /*#__PURE__*/React.createElement(React.Fragment, null);
  if (joinValues) {
    let value = selectedItems.map(i => stringifyValue(i[valueKey])).join(delimiter);
    return /*#__PURE__*/React.createElement("input", {
      disabled: disabled,
      name: name,
      type: "hidden",
      value: value
    });
  }
  return selectedItems.map((item, index) => /*#__PURE__*/React.createElement("input", {
    disabled: disabled,
    key: `hidden.${index}`,
    name: name,
    type: "hidden",
    value: stringifyValue(item[valueKey])
  }));
};
RenderHiddenFields.displayName = "RenderHiddenFields";
const MultiSelect = _ref2 => {
  let {
    selectedItems,
    showSelectAll,
    onBlur,
    readOnly,
    id,
    style,
    ...props
  } = _ref2;
  const [shouldSetFocus, setShouldSetFocus] = useState(false);
  const [searchText, setSearchText] = useControlledState(props.inputValue, props.onInputValueChange);
  const inputRef = useRef();
  const selectRef = useRef();
  const optionsRef = useRef();
  const rect = useRect(selectRef) || {};
  const innerId = useId();
  const labelId = id || `tui-multi-select-${innerId}`;
  useLayoutEffect(() => {
    const {
      current
    } = inputRef;
    if (current && shouldSetFocus) {
      current.focus();
      setShouldSetFocus(false);
    }
  }, [inputRef.current, shouldSetFocus]);
  function onRemove(item) {
    const items = [...selectedItems];
    const index = items.findIndex(i => i.value === item.value);
    items.splice(index, 1);
    props.onChange(items);
  }
  function onClearInput() {
    setSearchText("");
  }
  useLayoutEffect(() => {
    const inputMargin = 4;
    let styles = {
      top: `${rect.top + rect.height + inputMargin}px`,
      left: `${rect.left}px`,
      right: `${rect.right}px`,
      width: `${rect.width}px`,
      minHeight: `${rect.height}px`,
      transform: null,
      position: "fixed",
      height: null
    };
    const collidesWithBottom = window.innerHeight <= rect.bottom + MAX_RESULTS_BOX_HEIGHT;
    if (collidesWithBottom) {
      // when options' rect extends past the bottom of the window move it above the input
      styles.transform = `translateY(-100%) translateY(-${rect.height + inputMargin * 2}px)`;
      styles.height = `${MAX_OPTIONS_HEIGHT}px`;
    }
    Object.entries(styles).forEach(_ref3 => {
      let [key, value] = _ref3;
      optionsRef.current.style[key] = value;
    });
  }, [rect.top, rect.left, rect.right, rect.width, rect.height]);
  return /*#__PURE__*/React.createElement("div", {
    className: `multi-select ${props.className} ${props.error ? "has-error" : ""}`
  }, /*#__PURE__*/React.createElement(MultiDownshift, {
    itemToString: item => item ? item.label : "",
    selectedItems: selectedItems,
    onAdd: item => {
      props.onChange([...selectedItems, item]);
    },
    onRemove: onRemove,
    onOuterClick: event => {
      if (onBlur) {
        onBlur(event);
      }
    }
  }, _ref4 => {
    let {
      getLabelProps,
      getInputProps,
      getItemProps,
      getMenuProps,
      isOpen,
      openMenu,
      closeMenu,
      selectHighlightedItem,
      setHighlightedIndex
    } = _ref4;
    let {
      options
    } = props;
    if (props.shouldFilter) {
      options = options.filter(_ref5 => {
        var _label$toUpperCase;
        let {
          label
        } = _ref5;
        return label === null || label === void 0 ? void 0 : (_label$toUpperCase = label.toUpperCase()) === null || _label$toUpperCase === void 0 ? void 0 : _label$toUpperCase.includes(searchText === null || searchText === void 0 ? void 0 : searchText.toUpperCase());
      });
    }
    function onExpand(e) {
      if (props.disabled || props.readOnly) return;
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    }
    const allOptionsValues = options.map(item => get(item, "value", 0));
    const allSelectedValues = selectedItems.map(item => get(item, "value", 0));
    const allSelected = allOptionsValues.every(itemValue => {
      return allSelectedValues.includes(itemValue);
    });
    return /*#__PURE__*/React.createElement("div", {
      style: {
        position: "relative",
        ...style
      }
    }, props.label && /*#__PURE__*/React.createElement(Label, _extends({}, getLabelProps(), {
      htmlFor: labelId
    }), props.label), /*#__PURE__*/React.createElement("div", getInputProps({
      onClick: () => {
        if (props.disabled || props.readOnly) return;
        openMenu();
        if (!isOpen) {
          setShouldSetFocus(true);
        }
      },
      className: classNames({
        "is-open": isOpen,
        disabled: props.disabled,
        "read-only": readOnly
      }),
      ref: selectRef
    }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "tag-wrapper"
    }, selectedItems.map(item => /*#__PURE__*/React.createElement(Tag, {
      key: item.value,
      onClose: () => onRemove(item)
    }, item.label))), selectedItems.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "counter-wrapper"
    }, /*#__PURE__*/React.createElement(Tag, {
      className: "counter",
      onClose: () => props.onChange([])
    }, "(", selectedItems.length, ")"))), /*#__PURE__*/React.createElement("span", {
      role: "button",
      tabIndex: "0",
      className: "expand-icon",
      onClick: onExpand,
      onKeyPress: onExpand
    }, /*#__PURE__*/React.createElement(ExpandMore, null)), selectedItems.length === 0 && /*#__PURE__*/React.createElement("div", {
      className: "placeholder"
    }, props.placeholder)), /*#__PURE__*/React.createElement("div", {
      ref: optionsRef,
      className: "results-box"
    }, isOpen && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Input, {
      ref: inputRef,
      fluid: true,
      icon: true,
      placeholder: props.inputPlaceholder,
      onChange: e => setSearchText(e.target.value),
      value: searchText,
      id: labelId
    }, /*#__PURE__*/React.createElement("input", {
      style: {
        paddingLeft: 38
      }
    }), /*#__PURE__*/React.createElement("i", {
      className: "icon svg-wrapper",
      style: {
        left: 0
      }
    }, /*#__PURE__*/React.createElement(Search, null)), searchText && /*#__PURE__*/React.createElement("i", {
      role: "button",
      tabIndex: "0",
      className: "icon link svg-wrapper",
      onKeyPress: onEnter(onClearInput),
      onClick: onClearInput
    }, /*#__PURE__*/React.createElement(Close, {
      className: "close-icon"
    }))), options.length === 0 ? /*#__PURE__*/React.createElement("div", {
      className: "no-results"
    }, props.noResultsText) : /*#__PURE__*/React.createElement("ul", getMenuProps({
      tabIndex: -1,
      onKeyPress: onSpace(e => e.stopPropagation())
    }), /*#__PURE__*/React.createElement("li", {
      className: "select-all",
      role: "option",
      "aria-selected": "false"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      disabled: selectedItems.length === 0,
      onClick: () => props.onChange([])
    }, "Clear Selection")), /*#__PURE__*/React.createElement(Divider, null), showSelectAll ? /*#__PURE__*/React.createElement("li", {
      role: "option",
      "area-selected": String(allSelected),
      tabIndex: 0,
      onClick: () => {
        if (allSelected) props.onChange([]);else props.onChange(options);
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(CheckboxIcon, {
      tabIndex: "-1",
      checked: allSelected
    }), /*#__PURE__*/React.createElement("span", null, "All"))) : /*#__PURE__*/React.createElement(React.Fragment, null), options.map((item, index) => {
      const isSelected = selectedItems.findIndex(i => i.value === item.value) !== -1;
      return /*#__PURE__*/React.createElement("li", _extends({
        key: item.value
      }, getItemProps({
        item,
        index,
        onFocus: () => {
          setHighlightedIndex(index);
        },
        onKeyPress: onSpace(selectHighlightedItem, onEnter(selectHighlightedItem)),
        tabIndex: 0
      })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(CheckboxIcon, {
        tabIndex: "-1",
        checked: isSelected
      }), /*#__PURE__*/React.createElement("span", null, item.label)));
    })))));
  }), props.error && !isEmpty(props.error) ? /*#__PURE__*/React.createElement("div", {
    className: "tui-invalid-feedback",
    children: props.error
  }) : "", /*#__PURE__*/React.createElement(RenderHiddenFields, _extends({
    selectedItems: selectedItems
  }, props)));
};
MultiSelect.displayName = "MultiSelect";
MultiSelect.defaultProps = {
  label: undefined,
  placeholder: "Select option(s)...",
  inputPlaceholder: "Search...",
  noResultsText: "No Results found.",
  className: "",
  inputValue: "",
  onInputValueChange: undefined,
  shouldFilter: true,
  disabled: false,
  error: "",
  showSelectAll: false,
  readOnly: false,
  joinValues: false,
  valueKey: "value",
  delimiter: ",",
  style: {}
};
MultiSelect.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  selectedItems: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  noResultsText: PropTypes.string,
  className: PropTypes.string,
  inputValue: PropTypes.string,
  onInputValueChange: PropTypes.func,
  shouldFilter: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  showSelectAll: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  joinValues: PropTypes.bool,
  valueKey: PropTypes.string,
  delimiter: PropTypes.string,
  style: PropTypes.object
};
export default MultiSelect;
//# sourceMappingURL=MultiSelect.js.map